import { uniq } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RecommendationSource } from '~/api/Catalog';
import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { Checkbox } from '~/components/isolated/Checkbox';
import { SIZE } from '~/components/isolated/Checkbox/constants';
import Typography, { VARIANT } from '~/components/isolated/Typography';
import { ModalType } from '~/components/Modal/interface';
import ProductCardC from '~/components/ProductCardV3/variants/ProductCardC';
import Section from '~/components/Section';
import { useModal } from '~/hooks/useModal';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  cartStore,
  catalogStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { StartVerificationLocation } from '~/stores/MainStore';
import { isProductBundle } from '~/types/Product';
import { convertCartItemToProduct } from '~/utils/convertCartItemToProduct';

import OrderSummary from '../OrderSummary';

import styles from './CartContentV3.module.scss';

/**
 * Main cart content component for CartV3
 * Uses ProductCardC horizontal cards and OrderAgainSection slider
 */
const CartContentV3 = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const navigate = useNavigate();

  const analyticsTrackedRef = useRef(false);

  useEffect(() => {
    cartStore.promotionStore.loadStoredPromoCodes();
  }, []);

  useEffect(() => {
    if (!analyticsTrackedRef.current) {
      analyticsTrackedRef.current = true;
      analyticsService.track(AnalyticsEvent.VIEW_CART, {
        currency: orderStore.currency.toUpperCase(),
        value: mainStore.toFloat(cartStore.finalPrice),
        items: cartStore.cartForFirebase,
      });
      analyticsService.track(AnalyticsEvent.VIEW_CART_BI, {
        cart_id: undefined,
        in_opening_hours: false,
        products_amount: cartStore.cart.reduce(
          (sum, item) => sum + item.count,
          0,
        ),
        items_amount: cartStore.cart.length,
        price: cartStore.totalCartPrice.base,
        final_price: cartStore.finalPrice,
        eta_min: orderStore.etaCalculation?.duration.min || 0,
        eta_max: orderStore.etaCalculation?.duration.max || 0,
        delivery_fee: orderStore.fee.shippingPounds || 0,
        threshold: orderStore.fee.thresholdPounds || 0,
        is_surger: orderStore.etaCalculation?.highDemand || false,
      });
    }
    if (cartStore.cart.length) {
      const skus = cartStore.cart
        .map((cartItem) => {
          const isBundle = isProductBundle(cartItem);

          if (!isBundle) {
            return cartItem.sku;
          }

          return cartItem.offers.map((offer) => offer.sku);
        })
        .flat();

      catalogStore.getProductRecommendationDebounced(uniq(skus), [
        ...(company.showMatchingProducts_CartPage
          ? [RecommendationSource.CART]
          : []),
      ]);
    }

    return () => {
      catalogStore.clearProductRecommendations(RecommendationSource.CART);
    };
  }, []);

  const handleEmptyCart = () => {
    analyticsService.track(AnalyticsEvent.PURCHASE_CART_DELETED, {
      cart_id: undefined,
      products_amount: cartStore.cart.reduce(
        (sum, item) => sum + item.count,
        0,
      ),
      items_amount: cartStore.cart.length,
      price: cartStore.totalCartPrice.base,
      final_price: cartStore.finalPrice,
      eta_min: orderStore.etaCalculation?.duration.min ?? 0,
      eta_max: orderStore.etaCalculation?.duration.max ?? 0,
      delivery_fee: orderStore.fee.shippingPounds ?? 0,
      threshold: orderStore.fee.thresholdPounds ?? 0,
      is_surger: orderStore.etaCalculation?.highDemand ?? false,
    });
    cartStore.emptySelectedItemsFromCart();
  };

  const handleCheckout = () => {
    if (!userStore.isAuthorized) {
      openModal(ModalType.Auth);
      return;
    }

    if (
      userStore.isAuthorized &&
      userStore.personalData.kycStatus !== KYCStatus.Verified
    ) {
      mainStore.setStartVerificationLocation(StartVerificationLocation.CART);
      userStore
        .requestKYCSession(
          `${window.location.origin}${window.location.pathname}`,
        )
        .then(({ data }) => {
          const shouldOpenInAppBrowser =
            mainStore.isRN &&
            Number(mainStore.appVersion.replaceAll('.', '')) >= 1029;

          if (shouldOpenInAppBrowser) {
            mainStore.sendToRN('openInAppBrowser', { url: data.url });
          } else {
            window.location.href = data.url;
          }
        })
        .catch(() => void 0);
      return;
    }
  };

  const handleNavigateToCheckout = async () => {
    try {
      const cartCountChanged = await cartStore.calculateCart();

      if (cartCountChanged) {
        return;
      }

      navigate('/checkout');
    } catch (error) {
      console.error('Failed to calculate cart:', error);
      // TODO: Show error notification to user
    }
  };

  const isLoading =
    orderStore.isLoading ||
    orderStore.isETALoading ||
    cartStore.calculationProcess.isLoading ||
    cartStore.promotionStore.applyPromocodeMeta.isLoading;

  const isWarehouseClosed =
    orderStore.etaCalculation?.warehouse.availability.availableNow === false;

  return (
    <>
      <div className={styles.mainLayout}>
        <div className={styles.productsColumn}>
          <div className={styles.cartHeader}>
            <button
              data-button="delete"
              className={styles.deleteButton}
              onClick={handleEmptyCart}
              disabled={cartStore.selectedItemsTotalCount === 0}
            >
              <Icon type="mark-3.1" />
              <Typography
                variant={VARIANT['Text 200/Regular']}
                className={styles.deleteButtonText}
              >
                {t('deleteSelected')} ({cartStore.selectedItemsTotalCount})
              </Typography>
            </button>

            <Checkbox
              checked={cartStore.isSelectedAll}
              onChange={cartStore.handleSelectAllCartItems}
              label={t('selectAll')}
              labelFirst
              size={SIZE.M}
            />
          </div>

          <Section className={styles.cartContent}>
            <ul className={styles.cartList}>
              {cartStore.cart.map((offer) => (
                <li className={styles.cartListItem} key={offer.sku}>
                  <ProductCardC
                    product={convertCartItemToProduct(offer)}
                    isSelected={offer.selected}
                    onSelectionChange={() => {
                      if (offer.sku) {
                        cartStore.handleSelectedCartItem(offer.sku);
                      }
                    }}
                  />
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <div className={styles.checkoutColumn}>
          <OrderSummary
            onNavigateToCheckout={handleNavigateToCheckout}
            onCheckout={handleCheckout}
            isLoading={isLoading}
            isWarehouseClosed={isWarehouseClosed}
          />
        </div>
      </div>
    </>
  );
};

export default observer(CartContentV3);
